import { FC, Fragment, ReactNode } from "react"
import { Combobox, Transition } from "@headlessui/react"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

export interface Option {
  id: string
  text: string
}

interface Props {
  name: string
  query: string
  label?: string
  hideLabel?: boolean
  value?: Option
  placeholder?: string
  options: Option[]
  direction?: "vertical" | "horizontal"
  isLoading?: boolean
  required?: boolean
  renderOption?: (
    option: Option,
    state: { selected: boolean; active: boolean }
  ) => ReactNode
  footer?: ReactNode
  onChange: (val: Option) => void
  onChangeQuery: (val: string) => void
}

const Autocomplete: FC<Props> = ({
  name,
  label,
  hideLabel = false,
  value,
  query,
  placeholder,
  options,
  direction = "vertical",
  isLoading = false,
  required = false,
  renderOption,
  footer,
  onChange,
  onChangeQuery
}) => {
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.target.value
    onChangeQuery(newQuery)
    if (!newQuery && value?.id) onChange({ id: "", text: "" })
  }

  return (
    <div
      className={`w-full ${
        direction === "horizontal" ? "flex items-center" : ""
      }`}
    >
      <label
        htmlFor={name}
        className={hideLabel ? "sr-only" : "block font-medium"}
      >
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      <Combobox value={value} onChange={onChange}>
        <div
          className={`relative ${
            direction === "vertical"
              ? "mt-1"
              : hideLabel
              ? "flex-1"
              : "ml-3 flex-1"
          }`}
        >
          <div className="relative flex items-center w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-300 sm:text-sm shadow-sm py-3">
            <Combobox.Input
              className="w-full border-none px-3 py-px pr-10 leading-5 text-gray-900 focus:ring-0 appearance-none"
              displayValue={(option: Option) => option?.text}
              onChange={handleInputChange}
              placeholder={placeholder}
              autoComplete="new-location"
              id={name}
            />
            {isLoading ? (
              <Loader2
                className="animate-spin h-6 w-6 mr-2 text-primary-alt-400"
                aria-hidden="true"
              />
            ) : (
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronsUpDown
                  size={18}
                  className="text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
            )}
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => onChangeQuery("")}
          >
            <Combobox.Options
              static={!!footer}
              className="absolute z-50 mt-1 w-full rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm overflow-hidden"
            >
              <div className="max-h-60 overflow-auto py-1">
                {options?.length === 0 && query !== "" ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                    Nothing found.
                  </div>
                ) : (
                  options.map(option => (
                    <Combobox.Option
                      key={option.id}
                      className={({ active }) =>
                        `relative cursor-default select-none ${
                          renderOption ? "px-4 py-3" : "py-2 pl-10 pr-4"
                        } ${
                          active ? "bg-primary-400 text-white" : "text-gray-900"
                        }`
                      }
                      value={option}
                    >
                      {({ selected, active }) => (
                        <>
                          {renderOption ? (
                            renderOption(option, { selected, active })
                          ) : (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                {option.text}
                              </span>
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? "text-white" : "text-primary-400"
                                  }`}
                                >
                                  <Check size={16} aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </div>
              {footer}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  )
}

export default Autocomplete
